import * as Analytics from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';

type InventoryTableModalActionOrigin = ActionOrigin.NOTEBOOK;
type InventoryTableActionType =
  | 'create_and_fill'
  | 'fill_existing'
  | 'update_existing'
  | 'create_or_fill_existing'
  | null;
type InventoryTableType = 'transfer' | 'legacy_transfer' | 'inventory';

interface InventoryTableModalTableActionParams {
  tableActionModesShown: Array<InventoryStructuredTableMode>;
  tableActionModeChosen: InventoryStructuredTableMode;
}

interface InventoryTableModalSubmitParams {
  schemaSelected: boolean;
  schemaId: string | null;
  tableTypeAdded: InventoryTableType;
}

export interface InventoryTableModalTrackingParams {
  module: keyof typeof ANALYTICS_MODULE;
  actionOrigin: InventoryTableModalActionOrigin;
  entryId: string | null;
  tableActionParams?: InventoryTableModalTableActionParams;
  submitParams?: InventoryTableModalSubmitParams;
}

interface BaseMetadata {
  action_origin: ActionOrigin;
  entry_id?: string | null;
  file_id?: string | null;
  table_action_chosen?: string | null;
  table_actions_shown?: Array<string | null>;
  table_type_added?: string;
  schema_selected?: boolean;
  schema_id?: string | null;
}

function getTableActionTypeFromMode(mode: InventoryStructuredTableMode): InventoryTableActionType {
  switch (mode) {
    case InventoryStructuredTableMode.CREATE:
      return 'create_and_fill';
    case InventoryStructuredTableMode.FILL:
      return 'fill_existing';
    case InventoryStructuredTableMode.UPDATE_CONTAINERS:
      return 'update_existing';
    case InventoryStructuredTableMode.LEGACY_TRANSFER:
      return 'create_or_fill_existing';
    default:
      assertAndContinue(false, `Unexpected inventory table mode ${mode}`);
      return null;
  }
}

function getMetadataFromParams(params: InventoryTableModalTrackingParams): BaseMetadata {
  const tableActionMetadata =
    params.tableActionParams != null
      ? {
          table_action_chosen: getTableActionTypeFromMode(params.tableActionParams.tableActionModeChosen),
          table_actions_shown: params.tableActionParams.tableActionModesShown.map((actionMode) =>
            getTableActionTypeFromMode(actionMode)
          ),
        }
      : {};
  const submitMetadata =
    params.submitParams != null
      ? {
          schema_selected: params.submitParams.schemaSelected,
          schema_id: params.submitParams.schemaId,
          table_type_added: params.submitParams.tableTypeAdded,
        }
      : {};
  const fileIdKey = getEntryOrFileIdKey(params.entryId);
  return {
    action_origin: params.actionOrigin,
    [fileIdKey]: params.entryId,
    ...tableActionMetadata,
    ...submitMetadata,
  };
}

export function trackModalOpen(params: InventoryTableModalTrackingParams): void {
  Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE[params.module], {
    ...getMetadataFromParams(params),
    modalType: 'insert_inventory_table',
  });
}

export function trackModalClose(params: InventoryTableModalTrackingParams): void {
  Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, ANALYTICS_MODULE[params.module], {
    ...getMetadataFromParams(params),
    modalType: 'insert_inventory_table',
  });
}

export function trackModalSubmit(params: InventoryTableModalTrackingParams): void {
  Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, ANALYTICS_MODULE[params.module], {
    ...getMetadataFromParams(params),
    modalType: 'insert_inventory_table',
  });
}
